import { useEffect, useMemo, useRef, useState } from 'react';
import { loadEmojiGroups, searchEmojis } from '../emoji/registry';
import { applySkinTone, loadSavedSkinTone, saveSkinTone } from '../emoji/skinTone';
import { SKIN_TONES } from '../emoji/types';
import type { EmojiEntry, EmojiGroup, SkinTone } from '../emoji/types';

const SKIN_TONE_LABELS_FI: Record<SkinTone, string> = {
  light: 'Vaalea',
  'medium-light': 'Keskivaalea',
  medium: 'Keskitaso',
  'medium-dark': 'Keskitumma',
  dark: 'Tumma',
};

const SKIN_TONE_SWATCHES: Record<SkinTone, string> = {
  light: '🏻',
  'medium-light': '🏼',
  medium: '🏽',
  'medium-dark': '🏾',
  dark: '🏿',
};

const CELL_SIZE_PX = 64;
const ROW_GAP_PX = 6;
const OVERSCAN_ROWS = 4;

interface VirtualGridProps {
  entries: EmojiEntry[];
  tone: SkinTone | null;
  onPick: (emoji: string) => void;
}

function VirtualGrid({ entries, tone, onPick }: VirtualGridProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [layout, setLayout] = useState({ cols: 0, height: 0, scrollTop: 0 });

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const measure = () => {
      const cols = Math.max(
        1,
        Math.floor((el.clientWidth + ROW_GAP_PX) / (CELL_SIZE_PX + ROW_GAP_PX)),
      );
      setLayout((prev) => ({ ...prev, cols, height: el.clientHeight }));
    };
    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    return () => ro?.disconnect();
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onScroll = () => setLayout((prev) => ({ ...prev, scrollTop: el.scrollTop }));
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  // Reset scroll when the entry list changes (group switch, new search results).
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.scrollTop = 0;
    setLayout((prev) => ({ ...prev, scrollTop: 0 }));
  }, [entries]);

  const rowHeight = CELL_SIZE_PX + ROW_GAP_PX;
  const cols = layout.cols || 1;
  const totalRows = Math.ceil(entries.length / cols);
  // When the container has no measured height (initial render or test env),
  // render every entry so consumers can still find them.
  const renderAll = layout.height === 0;
  const startRow = renderAll
    ? 0
    : Math.max(0, Math.floor(layout.scrollTop / rowHeight) - OVERSCAN_ROWS);
  const endRow = renderAll
    ? totalRows
    : Math.min(
        totalRows,
        Math.ceil((layout.scrollTop + layout.height) / rowHeight) + OVERSCAN_ROWS,
      );
  const visible = entries.slice(startRow * cols, endRow * cols);
  const padTop = startRow * rowHeight;
  const padBottom = Math.max(0, (totalRows - endRow) * rowHeight);

  return (
    <div className="emoji-grid" role="tabpanel" ref={containerRef}>
      {padTop > 0 && <div style={{ height: padTop }} aria-hidden="true" />}
      <div className="emoji-grid-row">
        {visible.map((entry) => {
          const rendered = applySkinTone(entry, tone);
          return (
            <button
              key={entry.emoji}
              type="button"
              className="emoji-cell"
              onClick={() => onPick(rendered)}
              aria-label={entry.label}
              title={entry.label}
            >
              {rendered}
            </button>
          );
        })}
      </div>
      {padBottom > 0 && <div style={{ height: padBottom }} aria-hidden="true" />}
    </div>
  );
}

export function EmojiPicker({
  onPick,
  onCancel,
}: {
  onPick: (emoji: string) => void;
  onCancel: () => void;
}) {
  const [groups, setGroups] = useState<EmojiGroup[] | null>(null);
  const [activeKey, setActiveKey] = useState<string>('');
  const [tone, setTone] = useState<SkinTone | null>(() => loadSavedSkinTone());
  const [query, setQuery] = useState<string>('');
  const [debouncedQuery, setDebouncedQuery] = useState<string>('');

  useEffect(() => {
    let alive = true;
    loadEmojiGroups('fi').then((loaded) => {
      if (!alive) return;
      setGroups(loaded);
      setActiveKey((prev) => prev || loaded[0]?.key || '');
    });
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    const id = setTimeout(() => setDebouncedQuery(query), 120);
    return () => clearTimeout(id);
  }, [query]);

  const trimmedQuery = debouncedQuery.trim();
  const searching = trimmedQuery.length > 0;

  const searchResults = useMemo(() => {
    if (!groups || !searching) return [];
    return searchEmojis(groups, trimmedQuery);
  }, [groups, trimmedQuery, searching]);

  const activeEntries = useMemo(() => {
    if (!groups) return [];
    if (searching) return searchResults;
    return groups.find((g) => g.key === activeKey)?.entries ?? [];
  }, [groups, searching, searchResults, activeKey]);

  const handleToneClick = (next: SkinTone | null) => {
    setTone(next);
    saveSkinTone(next);
    if (next === null || searching || !groups) return;
    const activeGroup = groups.find((g) => g.key === activeKey);
    const activeHasTone = activeGroup?.entries.some((e) => e.hasSkinTone) ?? false;
    if (activeHasTone) return;
    const fallback =
      groups.find((g) => g.key === 'people-body') ??
      groups.find((g) => g.entries.some((e) => e.hasSkinTone));
    if (fallback) setActiveKey(fallback.key);
  };

  return (
    <div className="emoji-picker" role="dialog" aria-label="Valitse emoji">
      <button type="button" className="emoji-backdrop" aria-label="Sulje" onClick={onCancel} />
      <div className="emoji-modal">
        <input
          type="search"
          className="emoji-search"
          placeholder="Etsi emoji…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Etsi emoji"
        />
        <div className="emoji-skin-tones" role="group" aria-label="Ihonsävy">
          <button
            type="button"
            className={`emoji-skin-swatch${tone === null ? ' active' : ''}`}
            onClick={() => handleToneClick(null)}
            aria-label="Neutraali"
            aria-pressed={tone === null}
          >
            ✋
          </button>
          {SKIN_TONES.map((t) => (
            <button
              key={t}
              type="button"
              className={`emoji-skin-swatch${tone === t ? ' active' : ''}`}
              onClick={() => handleToneClick(t)}
              aria-label={SKIN_TONE_LABELS_FI[t]}
              aria-pressed={tone === t}
            >
              <span aria-hidden="true">✋{SKIN_TONE_SWATCHES[t]}</span>
            </button>
          ))}
        </div>
        {tone !== null && (
          <p className="emoji-tone-hint" aria-hidden="true">
            Sävy koskee ihmis-emojeihin
          </p>
        )}
        {searching ? (
          <div className="emoji-results-count" aria-live="polite">
            {searchResults.length} osumaa
          </div>
        ) : (
          <div className="emoji-tabs" role="tablist">
            {(groups ?? []).map((g) => (
              <button
                key={g.key}
                type="button"
                role="tab"
                aria-selected={g.key === activeKey}
                className={`emoji-tab${g.key === activeKey ? ' active' : ''}`}
                onClick={() => setActiveKey(g.key)}
              >
                {g.label}
              </button>
            ))}
          </div>
        )}
        {groups === null ? (
          <div className="emoji-loading" role="status" aria-live="polite">
            Ladataan…
          </div>
        ) : (
          <VirtualGrid entries={activeEntries} tone={tone} onPick={onPick} />
        )}
      </div>
    </div>
  );
}
